// Lightweight filter/hook registry inspired by WordPress filters.
// Allows registration of sync/async callbacks that transform a value.

import { captureException } from "@sentry/browser";

type HookCallback<T> = (value: T, context?: Record<string, unknown>) => T | Promise<T>;

class FilterRegistry {
	private hooks = new Map<string, Array<{ id: number; fn: HookCallback<any>; priority: number }>>();
	private nextId = 1;

	add<T>(hookName: string, fn: HookCallback<T>, priority = 10): number {
		const list = (this.hooks.get(hookName) || []) as Array<{ id: number; fn: HookCallback<T>; priority: number }>;
		const id = this.nextId++;
		list.push({ id, fn, priority });
		list.sort((a, b) => a.priority - b.priority);
		this.hooks.set(hookName, list as any);
		return id;
	}

	remove(hookName: string, id: number): boolean {
		const list = this.hooks.get(hookName);
		if (!list) return false;
		const idx = list.findIndex((h) => h.id === id);
		if (idx === -1) return false;
		list.splice(idx, 1);
		if (list.length === 0) this.hooks.delete(hookName);
		return true;
	}

	async apply<T>(hookName: string, initial: T, context?: Record<string, unknown>): Promise<T> {
		const list = this.hooks.get(hookName) as Array<{ id: number; fn: HookCallback<T>; priority: number }> | undefined;
		let value = initial;
		if (!list) return value;
		for (const h of list) {
			try {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
				value = await h.fn(value, context);
			} catch (error) {
				captureException(error);
			}
		}
		return value;
	}
}

// Use a `globalThis`-backed singleton so that multiple bundles or different
// module instances still share the same registry at runtime. This avoids the
// "one module registers, another can't see it" problem when libs are
// bundled separately.
const GLOBAL_KEY = "__nx_paeonia_filter_registry_v1";
const globalRef = globalThis as any;
if (!globalRef[GLOBAL_KEY]) {
	globalRef[GLOBAL_KEY] = new FilterRegistry();
}
const filters: FilterRegistry = globalRef[GLOBAL_KEY];

export const addFilter = <T>(hookName: string, fn: HookCallback<T>, priority = 10): number => filters.add<T>(hookName, fn, priority);

export const removeFilter = (hookName: string, id: number): boolean => filters.remove(hookName, id);

export const applyFilters = async <T>(hookName: string, initial: T, context?: Record<string, unknown>): Promise<T> =>
	filters.apply<T>(hookName, initial, context);

export const debugFilters = (hook: string) => {
	const list = filters["hooks"].get(hook);
	console.log(filters);
	if (!list) {
		console.log(`No filters registered for hook: ${hook}`);
		return;
	}
	console.log(`Filters for hook: ${hook}`);
	list.forEach(({ id, priority }, index) => {
		console.log(`${index + 1}. ID: ${id}, Priority: ${priority}`);
	});
};

export default FilterRegistry;
